import { Alert, Flex, message, Radio, Tooltip } from 'antd';
import { Button } from '../../../button';
import {
  CompressOutlined,
  ExpandOutlined,
  SaveOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons';
import { useTheme } from '../../../../providers/theme';
import { getColors } from '../../../../components/utils/getColors';
import { useState, useCallback, useEffect } from 'react';
import ChartCorrelation from './components/chartCorrelation';
import { TWaterOilRelativePermeability } from '../../types';
import { useSelected } from '../../../../providers/selectedContext';
import {
  CorrelationComparisonMode,
  CorrelationEndpointSource,
  FziFunctions,
  SelectedFziFunctions,
  SelectedFziFunctionsOptions
} from '../../../../@types/projects/types';
import { saveSelectedFziFunctions } from '../../../../api/data';
import { useAppContext } from '../../../../providers/AppContext';
import { Typography } from '../../../typography';
import ColorSettings from '../../../../components/chart/colorSettings';
import {
  CalculationStrategies,
  ProjectSample,
  RelativePermeabilityAnalysisValues
} from '../../../../@types/samples/types';
import { transformChartData } from '../../utils/transformChartData';
import { useCorrelation } from '../../../../providers/Correlation';
import { useNotificationCenter } from '../../../../providers/NotificationCenter';
import { useDimensionControl } from '../../hook/useDimensionControl';
import {
  getCorrelationChartTitle,
  resolveEndpointValue
} from '../../utils/endpointValueResolution';
import {
  ENDPOINT_COMPARISON_MODE_LABELS,
  ENDPOINT_SOURCE_LABELS
} from '../../utils/endpointSourceConstants';

const CHART_STRATEGY = CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY;

const HEIGHT_OPTIONS = ['60%', '70%', '80%', '90%', '100%'];
const WIDTH_OPTIONS = ['19%', '24%', '32%', '49%', '100%'];

const transformWaterOilField = (
  samples: ProjectSample[],
  field:
    | keyof RelativePermeabilityAnalysisValues['water_oil']
    | 'kro_absolute'
    | 'krw_absolute',
  endpointSource: CorrelationEndpointSource,
  comparisonRole: 'primary' | 'secondary',
  color: string,
  selectedXAxis: 'fzi' | 'rqi'
) => {
  return samples.flatMap(sample => {
    const fzi = sample.input_sample_values.fzi || 0;
    const rqi = sample.input_sample_values.rqi || 0;
    const code = sample.sample_code;
    const isSelected =
      sample.selectedAnalyses[
      CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY
      ];
    const resolved = (() => {
      if (field === 'kro_absolute') {
        return resolveEndpointValue(
          endpointSource,
          sample.input_sample_values.kro_absolute,
          sample.k_rel_values?.water_oil?.true_kro_w
        );
      }
      if (field === 'krw_absolute') {
        return resolveEndpointValue(
          endpointSource,
          sample.input_sample_values.krw_absolute,
          sample.k_rel_values?.water_oil?.true_krw
        );
      }
      if (field === 'sor_w') {
        return resolveEndpointValue(
          endpointSource,
          sample.k_rel_values?.water_oil?.sor_w,
          sample.k_rel_values?.water_oil?.true_sor_w
        );
      }
      if (field === 'no_w') {
        return resolveEndpointValue(
          endpointSource,
          sample.k_rel_values?.water_oil?.no_w,
          sample.k_rel_values?.water_oil?.true_no_w
        );
      }
      if (field === 'nw') {
        return resolveEndpointValue(
          endpointSource,
          sample.k_rel_values?.water_oil?.nw,
          sample.k_rel_values?.water_oil?.true_nw
        );
      }
      return {
        value: sample.k_rel_values?.water_oil?.[field] ?? 0,
        missingRefinement: false
      };
    })();

    if (resolved.value == null) {
      return [];
    }

    const finalColor = isSelected ? color : 'rgba(100, 100, 100, 0.6)';
    const xAxisValue = selectedXAxis === 'fzi' ? fzi : rqi;
    return [
      transformChartData(
        resolved.value,
        xAxisValue,
        code,
        isSelected,
        finalColor,
        undefined,
        {
          source: endpointSource,
          comparisonRole
        }
      )
    ];
  });
};

export function WaterOilRelativePermeability() {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { upsertProject } = useAppContext();
  const {
    selectedWaterOilSamples,
    selectedXAxis,
    activeFziFunctions,
    endpointSource,
    setEndpointSource,
    comparisonMode,
    setComparisonMode,
    appliedEndpointSource,
    preferenceEndpointSource,
    setPreferenceEndpointSource,
    applyEndpointSourcePreference,
    isApplyingEndpointPreference,
    waterOilRefinementBlock,
    canUseRefinementEndpointSource,
    syncProjectFromServer
  } = useCorrelation();
  const { registerDerivedNotification } = useNotificationCenter();
  const [chartColors, setChartColors] = useState({
    scatter: '#f47936',
    line: '#72E2D8'
  });
  const [loadingSave, setLoadingSave] = useState(false);
  const [localFziFunctions, setLocalFziFunctions] = useState<SelectedFziFunctions>({});
  const [showWarning, setShowWarning] = useState(false);

  const selectedFziFunctions = activeFziFunctions;

  const {
    currentValue: chartHeight,
    increase: expand,
    decrease: compress
  } = useDimensionControl(HEIGHT_OPTIONS, 0, 'correlation.waterOil.chartHeightIndex');

  const {
    currentValue: containerWidth,
    increase: zoomIn,
    decrease: zoomOut
  } = useDimensionControl(WIDTH_OPTIONS, 2, 'correlation.waterOil.chartWidthIndex');

  const updateSelectedFziFunctions = useCallback(
    (
      fnName: keyof SelectedFziFunctions,
      value: SelectedFziFunctionsOptions
    ) => {
      let localShowWarning = false;

      setLocalFziFunctions(old => {
        const updatedLocalFziFunctions = { ...old, [fnName]: value };

        Object.entries(updatedLocalFziFunctions).forEach(([key, value]) => {
          const selected = selectedFziFunctions?.[key]?.selected;
          if (selected !== value) {
            localShowWarning = true;
            return;
          }
        });

        return updatedLocalFziFunctions;
      });

      setShowWarning(localShowWarning);
    },
    [selectedFziFunctions]
  );

  async function handleSaveSelectedFziFunctions() {
    if (!selectedInfo.project?.uid) return;

    try {
      setLoadingSave(true);
      const scopedSelected = {
        sor_w: localFziFunctions.sor_w,
        kro_absolute: localFziFunctions.kro_absolute,
        krw_absolute: localFziFunctions.krw_absolute,
        no_w: localFziFunctions.no_w,
        nw: localFziFunctions.nw,
        sor: localFziFunctions.sor,
      };
      const payload = {
        x_variable: selectedXAxis,
        selected: scopedSelected,
        section: 'water_oil' as const
      };

      const response = await saveSelectedFziFunctions(
        selectedInfo.project?.uid,
        payload
      );

      setSelectedInfo({ project: response });
      upsertProject(response);
      syncProjectFromServer(response);
      message.success('Selected FZI functions saved successfully');
      setShowWarning(false);
    } catch (error) {
      console.error(error);
      message.error('Error saving the selected FZI functions');
    } finally {
      setLoadingSave(false);
    }
  }

  const secondarySource: CorrelationEndpointSource =
    endpointSource === 'lab' ? 'refinement' : 'lab';

  const buildSeries = (
    field:
      | keyof RelativePermeabilityAnalysisValues['water_oil']
      | 'kro_absolute'
      | 'krw_absolute',
    includeComparison: boolean
  ) => {
    const primary = transformWaterOilField(
      selectedWaterOilSamples,
      field,
      endpointSource,
      'primary',
      chartColors.scatter,
      selectedXAxis
    );

    if (!includeComparison || comparisonMode !== 'overlay') {
      return primary;
    }

    const secondary = transformWaterOilField(
      selectedWaterOilSamples,
      field,
      secondarySource,
      'secondary',
      'rgba(120,120,120,0.45)',
      selectedXAxis
    );

    return [...primary, ...secondary];
  };

  const waterOilData: TWaterOilRelativePermeability = {
    sor_w: buildSeries('sor_w', true),
    kroAbsolute: buildSeries('kro_absolute', true),
    krwAbsolute: buildSeries('krw_absolute', true),
    no_w: buildSeries('no_w', false),
    nw: buildSeries('nw', false)
  }

  useEffect(() => {  
    if (selectedFziFunctions) {
      const newLocalState: SelectedFziFunctions = {};

      (Object.keys(selectedFziFunctions) as Array<keyof FziFunctions>).forEach(key => {
        newLocalState[key] = selectedFziFunctions[key]?.selected ?? undefined;
      });

      setLocalFziFunctions(newLocalState);
    } else {
      setLocalFziFunctions({});
    }
  }, [selectedFziFunctions]);

  useEffect(() => {
    registerDerivedNotification(
      'water-oil-fzi-unsaved',
      showWarning
        ? {
            id: 'water-oil-fzi-unsaved',
            kind: 'fzi-unsaved',
            severity: 'warning',
            title: 'Water-Oil correlation has unsaved FZI changes',
            message: 'Unsaved changes',
            count: 1,
            sampleCodes: [],
            createdAt: 'current',
            current: true,
            target: {
              type: 'section',
              view: 'correlation',
              anchorId: 'correlation-water-oil'
            }
          }
        : null
    );

    return () => {
      registerDerivedNotification('water-oil-fzi-unsaved', null);
    };
  }, [registerDerivedNotification, showWarning]);

  return (
    <section id="water-oil-correlation">
      <Typography.Heading level={4} style={{ padding: '1rem 1rem 0' }}>
        {`Relative permeability - Water Oil - ${selectedXAxis.toUpperCase()}`}
      </Typography.Heading>
      <div style={{ padding: '1rem' }}>
        <Flex
          wrap="wrap"
          align="center"
          gap="small"
          justify="space-between"
        >
          <Flex wrap="wrap" align="center" gap="small">
            <ColorSettings
              chartColors={chartColors}
              setChartColors={setChartColors}
            />
            <Tooltip title="Zoom in charts">
              <Button variant="ghost" size="sm" startIcon={<ZoomInOutlined />} onClick={zoomIn} />
            </Tooltip>
            <Tooltip title="Zoom out charts">
              <Button variant="ghost" size="sm" startIcon={<ZoomOutOutlined />} onClick={zoomOut} />
            </Tooltip>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={endpointSource}
              onChange={event =>
                setEndpointSource(event.target.value as CorrelationEndpointSource)
              }
            >
              <Radio.Button value="lab">
                {ENDPOINT_SOURCE_LABELS.lab}
              </Radio.Button>
              <Radio.Button
                value="refinement"
                disabled={waterOilRefinementBlock != null}
              >
                {ENDPOINT_SOURCE_LABELS.refinement}
              </Radio.Button>
            </Radio.Group>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={comparisonMode}
              onChange={event =>
                setComparisonMode(event.target.value as CorrelationComparisonMode)
              }
            >
              <Radio.Button value="off">
                {ENDPOINT_COMPARISON_MODE_LABELS.off}
              </Radio.Button>
              <Radio.Button value="overlay">
                {ENDPOINT_COMPARISON_MODE_LABELS.overlay}
              </Radio.Button>
            </Radio.Group>
            <Flex
              align="center"
              gap="small"
            >
              <span style={{ color: colors.text, fontSize: '12px' }}>
                Use for correlations
              </span>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={preferenceEndpointSource}
                onChange={event =>
                  setPreferenceEndpointSource(
                    event.target.value as CorrelationEndpointSource
                  )
                }
              >
                <Radio.Button value="lab">
                  {ENDPOINT_SOURCE_LABELS.lab}
                </Radio.Button>
                <Radio.Button
                  value="refinement"
                  disabled={!canUseRefinementEndpointSource}
                >
                  {ENDPOINT_SOURCE_LABELS.refinement}
                </Radio.Button>
              </Radio.Group>
              <Button
                variant="primary"
                onClick={applyEndpointSourcePreference}
                loading={isApplyingEndpointPreference}
                disabled={
                  preferenceEndpointSource === appliedEndpointSource ||
                  (preferenceEndpointSource === 'refinement' &&
                    !canUseRefinementEndpointSource)
                }
              >
                Apply
              </Button>
            </Flex>
            {showWarning && (
              <Alert
                message="Unsaved changes"
                type="warning"
                style={{
                  color: 'black',
                  backgroundColor: '#FFFF8F'
                }}
                showIcon
                closable
              />
            )}
            {preferenceEndpointSource !== appliedEndpointSource && (
              <Alert
                message="Endpoint source has unsaved changes."
                type="info"
                style={{
                  color: 'black',
                  backgroundColor: '#e6f4ff'
                }}
                showIcon
              />
            )}
          </Flex>
          <Flex wrap="wrap" align="center" gap="small">
            <Tooltip title="Expand charts">
              <Button variant="ghost" size="sm" startIcon={<ExpandOutlined />} onClick={expand} />
            </Tooltip>
            <Tooltip title="Compress charts">
              <Button variant="ghost" size="sm" startIcon={<CompressOutlined />} onClick={compress} />
            </Tooltip>
            <Tooltip title="Update selected FZI functions">
              <Button variant="ghost" size="sm" startIcon={<SaveOutlined />} disabled={loadingSave} loading={loadingSave} onClick={handleSaveSelectedFziFunctions} />
            </Tooltip>
          </Flex>
        </Flex>
        <Flex wrap="wrap" gap="small" justify="space-between">
          <ChartCorrelation
            received={waterOilData.sor_w || []}
            analysisType={CHART_STRATEGY}
            field="sor_w"
            name={getCorrelationChartTitle(
              'sor_w',
              endpointSource,
              'Sor Correlation'
            )}
            xAxis={selectedXAxis.toUpperCase()}
            yAxis="Sor"
            chartType={'line'}
            containerWidth={containerWidth}
            height={chartHeight}
            updateSelectedFziFunctions={updateSelectedFziFunctions}
            chartColors={chartColors}
          />
          <ChartCorrelation
            received={waterOilData.kroAbsolute || []}
            analysisType={CHART_STRATEGY}
            field="kro_absolute"
            name={getCorrelationChartTitle(
              'kro_absolute',
              endpointSource,
              'Kro Absolute Correlation'
            )}
            xAxis={selectedXAxis.toUpperCase()}
            yAxis="Kro Absolute"
            chartType={'line'}
            containerWidth={containerWidth}
            height={chartHeight}
            updateSelectedFziFunctions={updateSelectedFziFunctions}
            chartColors={chartColors}
          />
          <ChartCorrelation
            received={waterOilData.krwAbsolute || []}
            analysisType={CHART_STRATEGY}
            field="krw_absolute"
            name={getCorrelationChartTitle(
              'krw_absolute',
              endpointSource,
              'Krw Absolute Correlation'
            )}
            xAxis={selectedXAxis.toUpperCase()}
            yAxis="Krw Absolute"
            chartType={'line'}
            containerWidth={containerWidth}
            height={chartHeight}
            updateSelectedFziFunctions={updateSelectedFziFunctions}
            chartColors={chartColors}
          />
          <ChartCorrelation
            received={waterOilData.no_w || []}
            analysisType={CHART_STRATEGY}
            field="no_w"
            name={getCorrelationChartTitle(
              'no_w',
              endpointSource,
              'No Correlation'
            )}
            xAxis={selectedXAxis.toUpperCase()}
            yAxis="No"
            chartType={'line'}
            containerWidth={containerWidth}
            height={chartHeight}
            updateSelectedFziFunctions={updateSelectedFziFunctions}
            chartColors={chartColors}
          />
          <ChartCorrelation
            received={waterOilData.nw || []}
            analysisType={CHART_STRATEGY}
            field="nw"
            name={getCorrelationChartTitle(
              'nw',
              endpointSource,
              'Nw Correlation'
            )}
            xAxis={selectedXAxis.toUpperCase()}
            yAxis="Nw"
            chartType={'line'}
            containerWidth={containerWidth}
            height={chartHeight}
            updateSelectedFziFunctions={updateSelectedFziFunctions}
            chartColors={chartColors}
          />
        </Flex>
      </div>
    </section>
  );
}
